import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { syncConnectionApi, syncConnectionKeys } from '../api';

/**
 * Dòng "Đồng bộ với <nguồn> · `WEB-42` ↗" ở chi tiết task — APM-SYNC-h,
 * docs/plan-project-sync.md §7.4.
 *
 * Việc không đồng bộ (gần như mọi việc) ⇒ không render gì, kể cả lúc tải và
 * lúc lỗi: đây là chú thích phụ, không phải nội dung chính của việc.
 *
 * Link chỉ có khi nguồn khai `web_base_url` và server dựng được đường dẫn cùng
 * origin — URL do API ngoài trả không được thành link bấm được nếu không qua
 * cửa ấy (`externalTaskUrl` ở API).
 *
 * ⚠️ Không nhận asOf (luật #7): không vẽ đại lượng nào theo thời gian — chỉ
 * là liên kết hiện hành giữa hai hệ thống; liên kết đồng bộ nằm ngoài
 * `event_log` (Q4 của plan) nên không có trạng thái quá khứ để dựng.
 */
export function TaskSyncLine({
  projectId,
  taskId,
}: {
  readonly projectId: string;
  readonly taskId: string;
}): ReactNode {
  const { t } = useTranslation();
  const link = useQuery({
    queryKey: syncConnectionKeys.taskLink(projectId, taskId),
    queryFn: () => syncConnectionApi.taskLink(projectId, taskId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const sync = link.data?.sync ?? null;
  if (!sync) return null;

  const label = sync.disconnected
    ? t('projectSync.charter.taskLineDisconnected', { source: sync.source })
    : t('projectSync.charter.taskLine', { source: sync.source });

  return (
    <div
      className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1 whitespace-pre-wrap text-xs text-muted-foreground"
      data-testid="task-sync-line"
    >
      <RefreshCw aria-hidden className="size-3 shrink-0" />
      <span>{label}</span>
      {sync.externalCode ? (
        <>
          <span aria-hidden> · </span>
          {sync.url ? (
            <a
              href={sync.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-primary underline-offset-2 hover:underline"
              title={t('projectSync.charter.openExternal', {
                code: sync.externalCode,
                source: sync.source,
              })}
            >
              {sync.externalCode} ↗
            </a>
          ) : (
            <span className="font-mono">{sync.externalCode}</span>
          )}
        </>
      ) : null}
    </div>
  );
}
